import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FiFile, FiImage, FiUploadCloud, FiX } from "react-icons/fi";
import type { StorageObject, Taxonomie } from "../../API";
import { client } from "../../Clients";
import GatedButton from "../GatedButton";
import { LoadingSpinner } from "../LoadingSpinner";
import { Modal } from "../Modal";
import TaxonomyMultiSelect from "../TaxonomyMultiSelect";
import type { UploadKind } from "./types";
import { PUBLICATION_ACCEPT, PUBLICATION_EXTENSIONS, ROOT_PLACEHOLDER } from "./types";
import { detectUploadKind, extractErrorMessage, formatFileSize } from "./utils";

const WRITE_TOKEN = "feature:storage:write";

const KIND_META: Record<UploadKind, { label: string; description: string; accept: string; icon: React.ReactNode }> = {
	image: {
		label: "Images",
		description: "PNG, JPG, GIF, WebP, SVG, BMP : compressée automatiquement",
		accept: "image/*",
		icon: <FiImage className="h-4 w-4" aria-hidden />
	},
	publication: {
		label: "Publications",
		description: "PDF, DOC(X), PPT(X), TXT, RTF : texte des PDF indexé pour la recherche",
		accept: PUBLICATION_ACCEPT,
		icon: <FiFile className="h-4 w-4" aria-hidden />
	}
};

/** Le fichier correspond-il à la destination ? (types image/* vs extensions bureautiques) */
const fileMatchesKind = (file: File, kind: UploadKind): boolean => {
	if (kind === "image") return (file.type ?? "").startsWith("image/");
	const ext = (file.name.split(".").pop() ?? "").toLowerCase();
	return file.type === "application/pdf" || PUBLICATION_EXTENSIONS.has(ext);
};

/** Sous-dossier proposé d'après le dossier ouvert dans le navigateur (ex. images/photos → photos). */
const subfolderFromPath = (currentPath: string, kind: UploadKind): string => {
	if (!currentPath || currentPath === ROOT_PLACEHOLDER) return "";
	const root = kind === "image" ? "images" : "publications";
	if (currentPath === root) return "";
	return currentPath.startsWith(`${root}/`) ? currentPath.slice(root.length + 1) : "";
};

interface UploadModalProps {
	open: boolean;
	/** Fichier déjà choisi (glisser-déposer sur la page) : pré-remplit la zone. */
	initialFile: File | null;
	/** Dossier ouvert dans le navigateur : propose le même sous-dossier à l'upload. */
	currentPath: string;
	taxonomies: Taxonomie[];
	onClose: () => void;
	/** Upload réussi : le parent rafraîchit la vue et sélectionne le nouveau fichier. */
	onUploaded: (object: StorageObject) => void;
}

/**
 * Ajout d'un fichier au stockage : zone de glisser-déposer (ou clic), destination images/
 * vs publications/ détectée d'après le type du fichier (modifiable), progression d'envoi réelle
 * (onUploadProgress d'axios) et envoi gardé par feature:storage:write. Les appels restent
 * exactement POST /api/storage/upload/images|publications : seul l'habillage change.
 */
export const UploadModal: React.FC<UploadModalProps> = ({ open, initialFile, currentPath, taxonomies, onClose, onUploaded }) => {
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [file, setFile] = useState<File | null>(null);
	const [kind, setKind] = useState<UploadKind>("image");
	const [title, setTitle] = useState("");
	const [fileName, setFileName] = useState("");
	const [folder, setFolder] = useState("");
	const [taxonomyIds, setTaxonomyIds] = useState<string[]>([]);
	const [dragActive, setDragActive] = useState(false);
	const [rejected, setRejected] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [progress, setProgress] = useState<number | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	const submitting = progress != null;

	// Ouverture → repartir d'un formulaire propre (avec le fichier déposé sur la page, le cas échéant).
	useEffect(() => {
		if (!open) return;
		setTitle("");
		setFileName("");
		setTaxonomyIds([]);
		setRejected(null);
		setError(null);
		setProgress(null);
		setFile(null);
		setKind("image");
		setFolder(subfolderFromPath(currentPath, "image"));
		if (initialFile) {
			applyFile(initialFile);
		}
		// Volontairement déclenché sur `open` seul : ré-ouvrir doit réinitialiser, pas chaque frappe.
	}, [open]);

	// L'aperçu utilise une object URL du blob choisi : révoquée à chaque changement et au démontage.
	useEffect(() => {
		return () => {
			if (previewUrl) URL.revokeObjectURL(previewUrl);
		};
	}, [previewUrl]);

	const applyFile = (next: File | null) => {
		setRejected(null);
		setError(null);
		if (!next) {
			setFile(null);
			setPreviewUrl(null);
			return;
		}
		const detected = detectUploadKind(next);
		if (!detected) {
			setRejected(`Format non pris en charge : « ${next.name} ». Formats acceptés : images ou ${PUBLICATION_ACCEPT}.`);
			return;
		}
		setKind(detected);
		setFolder(subfolderFromPath(currentPath, detected));
		setFile(next);
		setPreviewUrl((prev) => {
			if (prev) URL.revokeObjectURL(prev);
			return next.type.startsWith("image/") || next.type === "application/pdf" ? URL.createObjectURL(next) : null;
		});
		if (!title.trim()) {
			const base = next.name.substring(0, next.name.lastIndexOf(".")) || next.name;
			setTitle(base.replace(/[-_]+/g, " ").trim());
			setFileName(base);
		}
	};

	const switchKind = (next: UploadKind) => {
		if (submitting || next === kind) return;
		setKind(next);
		setFolder(subfolderFromPath(currentPath, next));
		// Un fichier incompatible avec la nouvelle destination est abandonné (le serveur le refuserait).
		if (file && !fileMatchesKind(file, next)) {
			setFile(null);
			setPreviewUrl((prev) => {
				if (prev) URL.revokeObjectURL(prev);
				return null;
			});
		}
	};

	const canSubmit = Boolean(file) && title.trim().length > 0 && !submitting;

	const submit = async () => {
		if (!file || !canSubmit) return;
		setError(null);
		setProgress(0);
		try {
			const options = {
				onUploadProgress: (event: { loaded: number; total?: number }) => {
					if (event.total) {
						setProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
					}
				}
			};
			const trimmedTitle = title.trim();
			const trimmedFileName = fileName.trim() || undefined;
			const trimmedFolder = folder.trim().replace(/^\/+|\/+$/g, "") || undefined;
			const taxonomyFilter = taxonomyIds.length ? taxonomyIds.join(",") : undefined;
			const response =
				kind === "image"
					? await client.storage.apiStorageUploadImagesPost(trimmedTitle, file, trimmedFileName, trimmedFolder, taxonomyFilter, options)
					: await client.storage.apiStorageUploadPublicationsPost(trimmedTitle, file, trimmedFileName, trimmedFolder, taxonomyFilter, options);
			toast.success(`« ${trimmedTitle} » ajouté au stockage.`);
			onUploaded(response.data ?? {});
		} catch (err) {
			console.error("Échec de l'upload", err);
			setProgress(null);
			setError(extractErrorMessage(err) ?? "L'envoi a échoué. Vérifiez le fichier et réessayez.");
		}
	};

	const meta = KIND_META[kind];
	const accept = useMemo(() => `${KIND_META.image.accept},${KIND_META.publication.accept}`, []);

	return (
		<Modal show={open} onHide={() => (submitting ? undefined : onClose())} size="lg" centered>
			<Modal.Header closeButton>
				<div>
					<Modal.Title>Ajouter un fichier</Modal.Title>
					<p className="text-sm text-slate-500">Le fichier part dans le bucket admin, source de vérité des médias.</p>
				</div>
			</Modal.Header>
			<Modal.Body className="bg-slate-50">
				<div className="space-y-4">
					{/* ---- Destination ---- */}
					<p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">1 · Destination</p>
					<div className="grid gap-2 sm:grid-cols-2">
						{(Object.keys(KIND_META) as UploadKind[]).map((option) => (
							<button
								type="button"
								key={option}
								onClick={() => switchKind(option)}
								aria-pressed={kind === option}
								disabled={submitting}
								className={`rounded-xl border px-4 py-3 text-left transition ${
									kind === option
										? "border-[#005CA9] bg-[#005CA9]/5 ring-1 ring-[#005CA9]/25"
										: "border-slate-200 bg-white hover:border-slate-300"
								}`}
							>
								<span className={`flex items-center gap-1.5 text-sm font-semibold ${kind === option ? "text-[#005CA9]" : "text-slate-800"}`}>
									{KIND_META[option].icon} {KIND_META[option].label}
									<span className="ml-auto font-mono text-[10px] font-normal text-slate-400">{option === "image" ? "images/" : "publications/"}</span>
								</span>
								<span className="mt-1 block text-xs text-slate-500">{KIND_META[option].description}</span>
							</button>
						))}
					</div>

					{/* ---- Zone de dépôt ---- */}
					<p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">2 · Fichier</p>
					{!file ? (
						<div
							role="button"
							tabIndex={submitting ? -1 : 0}
							onClick={() => !submitting && inputRef.current?.click()}
							onKeyDown={(e) => {
								if ((e.key === "Enter" || e.key === " ") && !submitting) inputRef.current?.click();
							}}
							onDragOver={(e) => {
								// stopPropagation : la modale est portalisée, les événements React remontent
								// sinon jusqu'au voile de dépôt plein écran de la page.
								e.preventDefault();
								e.stopPropagation();
								if (!submitting) setDragActive(true);
							}}
							onDragLeave={(e) => {
								e.stopPropagation();
								setDragActive(false);
							}}
							onDrop={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setDragActive(false);
								if (!submitting) applyFile(e.dataTransfer?.files?.[0] ?? null);
							}}
							className={`flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed px-4 py-10 text-center transition-colors ${
								dragActive ? "border-sky-400 bg-sky-50" : "border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50"
							}`}
						>
							<FiUploadCloud className="h-7 w-7 text-slate-400" aria-hidden />
							<p className="text-sm font-medium text-slate-700">Déposez un fichier ici, ou cliquez pour parcourir</p>
							<p className="text-xs text-slate-500">La destination ({meta.label.toLowerCase()}) est détectée d'après le type du fichier.</p>
							<input
								ref={inputRef}
								type="file"
								accept={accept}
								aria-label="Choisir un fichier"
								disabled={submitting}
								className="hidden"
								onChange={(e) => {
									applyFile(e.target.files?.[0] ?? null);
									e.target.value = "";
								}}
							/>
						</div>
					) : (
						<div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
							{previewUrl && file.type.startsWith("image/") ? (
								<img src={previewUrl} alt="Aperçu" className="h-16 w-16 shrink-0 rounded-lg border border-slate-100 object-cover" />
							) : (
								<span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400">
									<FiFile className="h-6 w-6" aria-hidden />
								</span>
							)}
							<div className="min-w-0 flex-1">
								<p className="truncate text-sm font-semibold text-slate-900">{file.name}</p>
								<p className="text-xs text-slate-500">
									{formatFileSize(file.size)} · {file.type || "type inconnu"} → <span className="font-mono">{kind === "image" ? "images/" : "publications/"}</span>
								</p>
								{previewUrl && file.type === "application/pdf" && (
									<a href={previewUrl} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#005CA9] hover:underline">
										Prévisualiser le PDF
									</a>
								)}
							</div>
							<button
								type="button"
								onClick={() => applyFile(null)}
								disabled={submitting}
								title="Retirer le fichier"
								aria-label="Retirer le fichier"
								className="shrink-0 rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
							>
								<FiX className="h-4 w-4" />
							</button>
						</div>
					)}
					{rejected && <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">{rejected}</p>}

					{/* ---- Détails ---- */}
					<p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">3 · Détails</p>
					<div className="grid gap-3 sm:grid-cols-2">
						<label className="block">
							<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Titre *</span>
							<input
								type="text"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								disabled={submitting}
								placeholder="Illustration de la page d'accueil"
								className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
							/>
						</label>
						<label className="block">
							<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Nom de fichier</span>
							<input
								type="text"
								value={fileName}
								onChange={(e) => setFileName(e.target.value)}
								disabled={submitting}
								placeholder="illustration-accueil"
								className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-sm focus:border-slate-400 focus:outline-none"
							/>
							<span className="mt-1 block text-xs text-slate-400">L'extension vient du fichier envoyé.</span>
						</label>
						<label className="block">
							<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sous-dossier</span>
							<div className="mt-1 flex items-center overflow-hidden rounded-lg border border-slate-200 bg-white focus-within:border-slate-400">
								<span className="border-r border-slate-100 bg-slate-50 px-2.5 py-2 font-mono text-xs text-slate-400">
									{kind === "image" ? "images/" : "publications/"}
								</span>
								<input
									type="text"
									value={folder}
									onChange={(e) => setFolder(e.target.value)}
									disabled={submitting}
									placeholder="campagnes/2026"
									className="w-full px-3 py-2 font-mono text-sm focus:outline-none"
								/>
							</div>
						</label>
						<div>
							<TaxonomyMultiSelect
								label="Taxonomies"
								options={taxonomies}
								value={taxonomyIds}
								onChange={setTaxonomyIds}
								disabled={submitting}
								placeholder="Assigner des taxonomies"
							/>
						</div>
					</div>

					{/* ---- Progression / erreur ---- */}
					{submitting && (
						<div className="space-y-1">
							<div className="h-2 w-full overflow-hidden rounded-full bg-slate-100" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress ?? 0}>
								<div className="h-full rounded-full bg-[#005CA9] transition-all" style={{ width: `${progress ?? 0}%` }} />
							</div>
							<p className="text-xs text-slate-600">Envoi en cours… {progress ?? 0} %</p>
						</div>
					)}
					{error && (
						<p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
							{error}
						</p>
					)}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button
					type="button"
					onClick={onClose}
					disabled={submitting}
					className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					Annuler
				</button>
				<GatedButton
					token={WRITE_TOKEN}
					onClick={submit}
					disabled={!canSubmit}
					className="inline-flex items-center gap-1.5 rounded-full bg-[#005CA9] px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004a87] disabled:cursor-not-allowed disabled:opacity-60"
				>
					{submitting ? <LoadingSpinner size="xs" inline /> : <FiUploadCloud className="h-4 w-4" />} Envoyer
				</GatedButton>
			</Modal.Footer>
		</Modal>
	);
};
